import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  Paper,
  useMediaQuery,
  useTheme,
  Card,
  Stack,
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import api from "../../utils/api";

const adminCardSx = {
  borderRadius: "18px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  backgroundColor: "rgba(255,255,255,0.96)",
};

const ArchitecturalRequestsExportPage = () => {
  const [status, setStatus] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedType, setSelectedType] = useState("");
  const [selectedSubtype, setSelectedSubtype] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [membershipType, setMembershipType] = useState("");

  const [types, setTypes] = useState([]);
  const [subtypes, setSubtypes] = useState([]);
  const [colors, setColors] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [membershipTypeOptions, setMembershipTypeOptions] = useState([]);
  const [membershipTypeLoading, setMembershipTypeLoading] = useState(false);

  const theme = useTheme();
  const isSmall = useMediaQuery(theme.breakpoints.down("md"));
  const printRef = useRef(null);

  const safeDate = (d) => (d ? new Date(d).toLocaleDateString() : "");

  const getMemberFullName = (m) => {
    if (!m) return "";
    const first = (m.FamiliarName || m.FirstName || "").trim();
    const last = (m.LastName || "").trim();
    return `${first} ${last}`.trim();
  };

  const buildMembershipTypeOptionsFromRows = (rows) => {
    const uniq = new Set(
      (rows || [])
        .map((r) => r?.member?.ClubMembershipTypeCode)
        .filter(Boolean)
        .map((v) => String(v).trim())
    );
    return [...uniq].sort().map((code) => ({ value: code, label: code }));
  };

  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [typesRes, subtypesRes, statusesRes] = await Promise.all([
          api.get("/architectural-request-types"),
          api.get("/architectural-request-subtypes"),
          api.get("/statuses/architectural"),
        ]);
        setTypes(typesRes.data || []);
        setSubtypes(subtypesRes.data || []);
        setStatuses(statusesRes.data || []);
      } catch (err) {
        console.error("Error fetching filter data:", err);
      }
    };

    fetchFilters();
  }, []);

  useEffect(() => {
    const fetchColors = async () => {
      if (!selectedType) {
        setColors([]);
        setSelectedColor("");
        return;
      }

      try {
        const res = await api.get(
          `/architectural-request-colors?type_id=${selectedType}`
        );
        setColors(res.data || []);
      } catch (err) {
        console.error("Error fetching colors:", err);
        setColors([]);
      }
    };

    fetchColors();
  }, [selectedType]);

  const fetchMembershipTypeOptions = async () => {
    setMembershipTypeLoading(true);
    try {
      const params = new URLSearchParams();
      params.append("includeMember", "1");
      const { data } = await api.get(
        `/getArchitecturalRequestsReport?${params.toString()}`
      );
      setMembershipTypeOptions(buildMembershipTypeOptionsFromRows(data || []));
    } catch (e) {
      console.error("Membership type options fetch error:", e);
      setMembershipTypeOptions([]);
    } finally {
      setMembershipTypeLoading(false);
    }
  };

  useEffect(() => {
    fetchMembershipTypeOptions();
  }, []);

  const handleTypeChange = (e) => {
    setSelectedType(e.target.value);
    setSelectedSubtype("");
    setSelectedColor("");
  };

  const handleReset = () => {
    setStatus("");
    setStartDate("");
    setEndDate("");
    setSelectedType("");
    setSelectedSubtype("");
    setSelectedColor("");
    setPropertyId("");
    setMembershipType("");
    setFilteredData([]);
  };

  const handleSearch = async () => {
    if (startDate && endDate && new Date(startDate) > new Date(endDate)) {
      alert("Start Date must be before End Date.");
      return;
    }

    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (status) params.append("status", status);
      if (startDate) params.append("start", startDate);
      if (endDate) params.append("end", endDate);
      if (selectedType) params.append("type_id", selectedType);
      if (selectedSubtype) params.append("subtype_id", selectedSubtype);
      if (selectedColor) params.append("color_id", selectedColor);
      if (propertyId.trim()) params.append("property_id", propertyId.trim());
      if (membershipType.trim()) {
        params.append("club_membership_type_code", membershipType.trim());
      }
      params.append("includeMember", "1");

      const { data } = await api.get(
        `/getArchitecturalRequestsReport?${params.toString()}`
      );

      const flattened = (Array.isArray(data) ? data : []).map((row) => {
        const m = row.member || {};
        return {
          ...row,
          membership_type: m.ClubMembershipTypeCode ?? "",
          member_full_name: getMemberFullName(m),
          member_full_address: m.AlternateAddress || m.FullAddress || "",
          requested_at_display: safeDate(row.requested_at),
          reviewed_at_display: safeDate(row.reviewed_at),
          reviewed_by_display: row.reviewed_by_name || row.reviewed_by || "",
          color_display: row.option_color_name || "",
          scheme_display: row.scheme_name || "",
          acknowledged_display:
            row.acknowledged === 0 ||
            row.acknowledged === "0" ||
            row.acknowledged === false
              ? "Yes"
              : "No",
          anticipated_start_display: safeDate(
            row.anticipated_start_date || row.anticipatedStartDate
          ),
          anticipated_completion_display: safeDate(
            row.anticipated_completion_date || row.anticipatedCompletionDate
          ),
        };
      });

      setFilteredData(flattened);

      if (flattened.length) {
        setMembershipTypeOptions(buildMembershipTypeOptionsFromRows(flattened));
      }
    } catch (error) {
      console.error("Error fetching architectural requests:", error);
    } finally {
      setLoading(false);
    }
  };

  const columns = useMemo(
    () => [
      { field: "id", headerName: "Request ID", width: 110 },
      { field: "property_id", headerName: "Member ID", width: 120 },
      { field: "membership_type", headerName: "Membership Type", width: 140 },
      { field: "member_full_name", headerName: "Member Name", width: 180 },
      {
        field: "member_full_address",
        headerName: "Member Full Address",
        width: 300,
      },
      { field: "status", headerName: "Status", width: 140 },
      { field: "request_type_name", headerName: "Type", width: 160 },
      { field: "subtype_name", headerName: "Subtype", width: 160 },
      { field: "scheme_display", headerName: "Color Scheme", width: 180 },
      { field: "color_display", headerName: "Color", width: 140 },
      { field: "description", headerName: "Description", width: 400 },
      { field: "requested_at_display", headerName: "Requested At", width: 140 },
      { field: "reviewed_at_display", headerName: "Reviewed At", width: 140 },
      { field: "reviewed_by_display", headerName: "Reviewed By", width: 180 },
      { field: "acknowledged_display", headerName: "Acknowledged", width: 140 },
      {
        field: "anticipated_start_display",
        headerName: "Anticipated Start",
        width: 180,
      },
      {
        field: "anticipated_completion_display",
        headerName: "Anticipated Completion",
        width: 200,
      },
    ],
    []
  );

  const GRID_MIN_WIDTH = 2000;

  const initialColumnVisibility = useMemo(
    () =>
      isSmall
        ? { member_full_address: false, description: false }
        : { member_full_address: true, description: true },
    [isSmall]
  );

  return (
    <Box
      sx={{
        width: filteredData.length > 0 ? "65%" : "100%",
        transition: "width 0.3s ease",
      }}
    >
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        
        <Card
          elevation={0}
          sx={{
            ...adminCardSx,
            p: { xs: 2, md: 3 },
            mb: 3,
          }}
        >
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
            >
              Filters
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              Refine the export by member, membership type, status, request type, subtype, color, and date.
            </Typography>
          </Box>

          <Grid container spacing={2.5} alignItems="center">
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Member/Property ID"
                type="text"
                InputLabelProps={{ shrink: true }}
                value={propertyId}
                onChange={(e) => setPropertyId(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>ClubMembershipTypeCode</InputLabel>
                <Select
                  value={membershipType}
                  label="ClubMembershipTypeCode"
                  onChange={(e) => setMembershipType(e.target.value)}
                  disabled={membershipTypeLoading}
                  sx={{ borderRadius: "10px" }}
                >
                  <MenuItem value="">All</MenuItem>
                  {membershipTypeOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={status}
                  label="Status"
                  onChange={(e) => setStatus(e.target.value)}
                  sx={{ borderRadius: "10px" }}
                >
                  <MenuItem value="">All</MenuItem>
                  {statuses.map((s) => (
                    <MenuItem key={s.id} value={s.statusname}>
                      {s.statusname}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                label="Start Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                  },
                }}
              />
            </Grid>

            <Grid item xs={6} md={3}>
              <TextField
                fullWidth
                label="End Date"
                type="date"
                InputLabelProps={{ shrink: true }}
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                  },
                }}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={selectedType}
                  label="Type"
                  onChange={handleTypeChange}
                  sx={{ borderRadius: "10px" }}
                >
                  <MenuItem value="">All</MenuItem>
                  {types.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.type}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <FormControl fullWidth disabled={!selectedType}>
                <InputLabel>Subtype</InputLabel>
                <Select
                  value={selectedSubtype}
                  label="Subtype"
                  onChange={(e) => setSelectedSubtype(e.target.value)}
                  sx={{ borderRadius: "10px" }}
                >
                  <MenuItem value="">All</MenuItem>
                  {subtypes
                    .filter((s) => s.type_id === parseInt(selectedType || "-1", 10))
                    .map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.subtype_name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Grid>

            {colors.length > 0 && (
              <Grid item xs={12} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Color</InputLabel>
                  <Select
                    value={selectedColor}
                    label="Color"
                    onChange={(e) => setSelectedColor(e.target.value)}
                    sx={{ borderRadius: "10px" }}
                  >
                    <MenuItem value="">All</MenuItem>
                    {colors.map((c) => (
                      <MenuItem key={c.id} value={c.id}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <Box
                            sx={{
                              width: 16,
                              height: 16,
                              borderRadius: "50%",
                              backgroundColor: c.color,
                              border: "1px solid #ccc",
                            }}
                          />
                          {c.color}
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            )}

            <Grid item xs={12} md={colors.length > 0 ? 3 : 6}>
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                justifyContent={{ xs: "stretch", md: "flex-end" }}
              >
                <Button
                  fullWidth
                  variant="outlined"
                  color="secondary"
                  onClick={handleReset}
                  sx={{
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                >
                  Reset Filters
                </Button>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleSearch}
                  sx={{
                    backgroundColor: "#2f63ad",
                    borderRadius: "10px",
                    textTransform: "none",
                    fontWeight: 700,
                    boxShadow: "none",
                    "&:hover": {
                      backgroundColor: "#275592",
                      boxShadow: "0 8px 20px rgba(47,99,173,0.22)",
                    },
                  }}
                >
                  Search
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Card>

        <Card
          elevation={0}
          sx={{
            ...adminCardSx,
            p: { xs: 1.5, md: 2 },
          }}
        >
          <Box sx={{ mb: 2, px: 1 }}>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}
            >
              Results
            </Typography>
            <Typography variant="body2" sx={{ color: "#6b778c" }}>
              {loading
                ? "Loading architectural requests..."
                : filteredData.length > 0
                ? `${filteredData.length} record${filteredData.length !== 1 ? "s" : ""} found`
                : "No results to display"}
            </Typography>
          </Box>

          <Box mt={1}>
            {loading ? (
              <Box sx={{ p: 3 }}>
                <Typography sx={{ color: "#6b778c" }}>Loading...</Typography>
              </Box>
            ) : filteredData.length > 0 ? (
              <Box ref={printRef}>
                <Paper
                  elevation={0}
                  sx={{
                    borderRadius: "14px",
                    overflow: "hidden",
                    border: "1px solid rgba(47,99,173,0.08)",
                    backgroundColor: "rgba(255,255,255,0.98)",
                  }}
                >
                  <Box sx={{ width: "100%", overflowX: "auto" }}>
                    <Box
                      sx={{
                        display: "inline-block",
                        minWidth: `${GRID_MIN_WIDTH}px`,
                      }}
                    >
                      <DataGrid
                        rows={filteredData}
                        columns={columns}
                        getRowId={(r) =>
                          r.id ??
                          `${r.property_id}-${r.requested_at}-${r.request_type_name}`
                        }
                        autoHeight
                        disableRowSelectionOnClick
                        slots={{ toolbar: GridToolbar }}
                        slotProps={{
                          toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: { debounceMs: 300 },
                          },
                        }}
                        initialState={{
                          pagination: {
                            paginationModel: { pageSize: 25, page: 0 },
                          },
                          columns: {
                            columnVisibilityModel: initialColumnVisibility,
                          },
                        }}
                        pageSizeOptions={[10, 25, 50, 100]}
                        getRowHeight={() => "auto"}
                        sx={{
                          border: "none",
                          "& .MuiDataGrid-columnHeaders": {
                            backgroundColor: "rgba(47,99,173,0.04)",
                            color: "#223142",
                            fontWeight: 700,
                          },
                          "& .MuiDataGrid-cell": {
                            py: 1.25,
                            alignItems: "center",
                          },
                          "& .MuiDataGrid-toolbarContainer": {
                            p: 1.25,
                            borderBottom: "1px solid rgba(47,99,173,0.08)",
                          },
                          "& .MuiDataGrid-footerContainer": {
                            borderTop: "1px solid rgba(47,99,173,0.08)",
                          },
                        }}
                      />
                    </Box>
                  </Box>
                </Paper>

                <Typography
                  variant="body2"
                  sx={{ mt: 2, fontWeight: 700, color: "#223142" }}
                >
                  Total Records: {filteredData.length}
                </Typography>
              </Box>
            ) : (
              <Box sx={{ p: 3 }}>
                <Typography sx={{ color: "#6b778c" }}>
                  No results to display
                </Typography>
              </Box>
            )}
          </Box>
        </Card>
      </Box>
    </Box>
  );
};

export default ArchitecturalRequestsExportPage;
